import { RefreshCw } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useSyncEnrichment } from '@/api/publishing';

interface SyncToScoutAroundButtonProps {
  syncedLocationId: string;
  disabled?: boolean;
}

export function SyncToScoutAroundButton({
  syncedLocationId,
  disabled = false,
}: SyncToScoutAroundButtonProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const syncMutation = useSyncEnrichment();

  const handleSync = (): void => {
    syncMutation.mutate(syncedLocationId, {
      onSuccess: (response) => {
        setOpen(false);
        if (response.success) {
          toast({
            title: 'Enrichment synced to ScoutAround',
            description: `Updated ${String(response.updatedFields.length)} field(s)`,
          });
        } else {
          toast({
            title: 'Failed to sync enrichment',
            description: response.error ?? 'Unknown error',
            variant: 'destructive',
          });
        }
      },
      onError: (error) => {
        setOpen(false);
        toast({
          title: 'Failed to sync enrichment',
          description: error instanceof Error ? error.message : 'Unknown error',
          variant: 'destructive',
        });
      },
    });
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled || syncMutation.isPending}
          data-testid="sync-to-scoutaround-btn"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
          {syncMutation.isPending ? 'Syncing...' : 'Sync to ScoutAround'}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Sync Enrichment Changes</AlertDialogTitle>
          <AlertDialogDescription>
            This will sync the latest enrichment data to the published location on ScoutAround.
            The changes will be visible immediately on the ScoutAround platform.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleSync} disabled={syncMutation.isPending}>
            {syncMutation.isPending ? 'Syncing...' : 'Sync Changes'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
